import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import ColumnSelector from 'ats/components/column-selector/ColumnSelector';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import { externalApplicationStore } from './ExternalApplicationStore';
import {
	getExternalApplicationJobId,
	getExternalApplicationJobTitle,
	getExternalApplicationRoleName,
	getExternalApplicationSourceChipLabel,
	getExternalApplicationStatusLabel
} from '../open-jobs/externalApplicationOptions';

function ExternalApplicationsTable({
	data,
	onClickRow,
	sort,
	onSort,
	activeRowId,
	visibleColumns,
	onVisibleColumnsChange,
	isEmpty,
	emptyState
}) {
	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;
		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const resolvedVisibleColumns =
		visibleColumns || externalApplicationStore.defaultTableColumnsOptions;

	const shouldShowColumn = column =>
		resolvedVisibleColumns.some(
			visibleColumn => visibleColumn.value === column
		);

	const isProfileOpen =
		drawerStore.isOpen &&
		[
			drawerStore.DRAWER_IDS.EXTERNAL_APPLICATION_PROFILE,
			drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
			drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
			drawerStore.DRAWER_IDS.CLIENT_PROFILE
		].includes(drawerStore.drawerId);

	const isApplicationProfileOpen =
		drawerStore.isOpen &&
		drawerStore.drawerId ===
			drawerStore.DRAWER_IDS.EXTERNAL_APPLICATION_PROFILE;

	const renderLinkedIn = linkedinUrl =>
		linkedinUrl ? (
			<ActionMenu
				buttonIcon={
					<img
						src="/ats/icons/external-application-linkedin.svg"
						alt="LinkedIn"
					/>
				}
				tooltipText={linkedinUrl}
				items={[
					{
						label: 'Copy LinkedIn URL',
						img: '/ats/icons/copy-icon.svg',
						onClick: () => copyTextToClipboard(linkedinUrl),
						successLabel: 'LinkedIn URL Copied',
						successImg: '/ats/icons/check-icon.svg',
						resetDelay: 2000
					},
					{
						label: 'Open LinkedIn URL',
						img: '/ats/icons/send-icon.svg',
						onClick: () => window.open(linkedinUrl, '_blank')
					}
				]}
			/>
		) : (
			<div className="inactive-icon">
				<img
					src="/ats/icons/external-application-linkedin.svg"
					alt="LinkedIn"
				/>
			</div>
		);

	const renderStatus = status => {
		const resolved = status || 'pending';

		return (
			<span className={`external-application-status is-${resolved}`}>
				{getExternalApplicationStatusLabel(resolved)}
			</span>
		);
	};

	const renderTableHeader = () => (
		<thead>
			<tr>
				{shouldShowColumn('name') && (
					<th className="is-sortable" onClick={() => handleHeaderClick('name')}>
						Applicant Name {isColumnSorted('name')}
					</th>
				)}
				<th className="linkedin-column" />
				{isProfileOpen ? (
					<>
						{shouldShowColumn('jobTitle') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('jobTitle')}>
								Job Position Title {isColumnSorted('jobTitle')}
							</th>
						)}
						{shouldShowColumn('status') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('status')}>
								Status {isColumnSorted('status')}
							</th>
						)}
					</>
				) : (
					<>
						{shouldShowColumn('jobId') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('jobId')}>
								# Job {isColumnSorted('jobId')}
							</th>
						)}
						{shouldShowColumn('applicantRole') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('applicantRole')}>
								Applicant Role {isColumnSorted('applicantRole')}
							</th>
						)}
						{shouldShowColumn('jobTitle') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('jobTitle')}>
								Job Position Title {isColumnSorted('jobTitle')}
							</th>
						)}
						{shouldShowColumn('clientName') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('clientName')}>
								Client {isColumnSorted('clientName')}
							</th>
						)}
						{shouldShowColumn('appliedAt') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('appliedAt')}>
								Applied on {isColumnSorted('appliedAt')}
							</th>
						)}
						{shouldShowColumn('country') && <th>Country</th>}
						{shouldShowColumn('source') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('source')}>
								Source {isColumnSorted('source')}
							</th>
						)}
						{shouldShowColumn('status') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('status')}>
								Status {isColumnSorted('status')}
							</th>
						)}
					</>
				)}
				<th className="has-text-centered action-btn">
					{isProfileOpen ? null : (
						<ColumnSelector
							target={props => (
								<button type="button" className="action-btn" {...props}>
									<img src="/ats/icons/plus-icon.svg" alt="Add button" />
								</button>
							)}
							value={resolvedVisibleColumns}
							options={externalApplicationStore.tableColumnsOptions}
							onChange={values => onVisibleColumnsChange?.(values)}
						/>
					)}
				</th>
			</tr>
		</thead>
	);

	const renderTableRow = row => (
		<tr
			key={row.id}
			className={`${
				isApplicationProfileOpen && String(activeRowId) === String(row.id)
					? 'is-active'
					: ''
			}`}
			onClick={event => {
				event.stopPropagation();
				if (!['BUTTON', 'INPUT'].includes(event.target.tagName)) {
					onClickRow?.(row);
				}
			}}>
			{shouldShowColumn('name') && (
				<td className="name-column">{row.name || ''}</td>
			)}
			<td className="linkedin-column">{renderLinkedIn(row.linkedinUrl)}</td>
			{isProfileOpen ? (
				<>
					{shouldShowColumn('jobTitle') && (
						<td>{getExternalApplicationJobTitle(row.job)}</td>
					)}
					{shouldShowColumn('status') && <td>{renderStatus(row.status)}</td>}
				</>
			) : (
				<>
					{shouldShowColumn('jobId') && (
						<td>{getExternalApplicationJobId(row.job)}</td>
					)}
					{shouldShowColumn('applicantRole') && (
						<td>{getExternalApplicationRoleName(row.job) || '—'}</td>
					)}
					{shouldShowColumn('jobTitle') && (
						<td>{getExternalApplicationJobTitle(row.job)}</td>
					)}
					{shouldShowColumn('clientName') && (
						<td>{row.job?.client?.name || '—'}</td>
					)}
					{shouldShowColumn('appliedAt') && (
						<td>
							{row.appliedAt || row.createdAt
								? moment(row.appliedAt || row.createdAt).format('ll')
								: '—'}
						</td>
					)}
					{shouldShowColumn('country') && <td>{row.country?.name || '—'}</td>}
					{shouldShowColumn('source') && (
						<td>
							<AtsTooltip
								text={
									row.referrerUser?.name ? `By ${row.referrerUser.name}` : null
								}
								placement="bottom">
								<span className="external-application-source-chip">
									{getExternalApplicationSourceChipLabel(row.source)}
								</span>
							</AtsTooltip>
						</td>
					)}
					{shouldShowColumn('status') && <td>{renderStatus(row.status)}</td>}
				</>
			)}
			<td className="action-btn"></td>
		</tr>
	);

	return (
		<div className={`ats-table-scroll${isEmpty ? ' is-empty-results' : ''}`}>
			<table
				className={`table is-fullwidth${
					isProfileOpen ? ' is-short-list-view' : ''
				}`}>
				{renderTableHeader()}
				<tbody>{(data || []).map(renderTableRow)}</tbody>
			</table>
			{isEmpty ? (
				<div className="external-applications-empty">{emptyState}</div>
			) : null}
		</div>
	);
}

export default observer(ExternalApplicationsTable);
